import { Button, Stack, Typography } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import {
    AccessTime as AccessTimeIcon,
    ConfirmationNumber as ConfirmationNumberIcon,
    VisibilityOff as VisibilityOffIcon,
    WavingHand as WelcomeIcon,
} from '@mui/icons-material';
import { useAppDispatch } from '@/store/hooks';
import { open } from '@/store/uiSlice';
import { dismissWelcomeDialog } from '@/utils/helpers.ts';
import { useI18nContext } from '@/i18n/i18n-react';
import { useGetUserInfoQuery, useGetCategoryRequestsQuery } from '@/store/apiSlice';

const WelcomeDialog = ({
    open: propOpen,
    onClose: propClose,
}: {
    open: boolean;
    onClose: () => void;
}) => {
    const { LL } = useI18nContext();
    const dispatch = useAppDispatch();

    // Fetch user info locally so we can skip the category-requests query when not logged in
    const { data: userInfo } = useGetUserInfoQuery();

    const { data: categoryRequestsData } = useGetCategoryRequestsQuery(
        { status: 'pending' },
        { skip: !userInfo }
    );

    const pendingCount =
        categoryRequestsData && Array.isArray(categoryRequestsData.data)
            ? categoryRequestsData.data.length
            : 0;

    const handleRequestCategories = () => {
        dispatch(open('categoryRequest'));
        propClose();
    };

    const handleRemindLater = () => {
        propClose();
    };

    const handleDontShowAgain = () => {
        dismissWelcomeDialog();
        propClose();
    };

    const handleViewPending = () => {
        dispatch(open('categoryRequestsReview'));
        propClose();
    };

    return (
        <Dialog
            open={propOpen}
            onClose={propClose}
            maxWidth="md"
            aria-labelledby="welcome-dialog-title"
        >
            <DialogTitle id="welcome-dialog-title">
                <WelcomeIcon /> {LL.welcome.title()}
            </DialogTitle>
            <DialogContent>
                {pendingCount > 0 ? (
                    // Show alternate message when user already requested categories
                    <>
                        <Typography variant="body1">{LL.welcome.message()}</Typography>

                        <Typography variant="body2" sx={{ mt: 1 }}>
                            {LL.categoryRequests.pendingCount({ count: pendingCount })}
                        </Typography>

                        <Typography variant="body2" sx={{ mt: 1 }}>
                            {LL.welcome.pendingMessage()}
                        </Typography>
                    </>
                ) : (
                    <Typography variant="body1">{LL.welcome.message()}</Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                    <Button
                        startIcon={<AccessTimeIcon />}
                        onClick={handleRemindLater}
                        sx={{ flexGrow: 1, whiteSpace: 'nowrap' }}
                    >
                        {LL.welcome.remindLater()}
                    </Button>
                    <Button
                        startIcon={<VisibilityOffIcon />}
                        onClick={handleDontShowAgain}
                        sx={{ flexGrow: 1, whiteSpace: 'nowrap' }}
                    >
                        {LL.welcome.dismiss()}
                    </Button>
                    {pendingCount > 0 ? (
                        <Button
                            variant="contained"
                            startIcon={<ConfirmationNumberIcon />}
                            onClick={handleViewPending}
                            sx={{ flexGrow: 1, whiteSpace: 'nowrap' }}
                        >
                            {`${LL.categoryRequests.pendingRequests()} (${pendingCount})`}
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            startIcon={<ConfirmationNumberIcon />}
                            onClick={handleRequestCategories}
                            sx={{ flexGrow: 1, whiteSpace: 'nowrap' }}
                        >
                            {LL.welcome.requestCategories()}
                        </Button>
                    )}
                </Stack>
            </DialogActions>
        </Dialog>
    );
};

export default WelcomeDialog;
